// Adapted from davinci-elections apps/web/src/theme/recipes/button.ts @ 6e23f58
import { defineRecipe, defineStyle } from '@chakra-ui/react'

// vocdoni-app buttons: 6 px radius, regular weight, 14 px (16 px on the member pages, see tokens.ts).
// A label never clips or spills out of its button (mobile review, ruling M1): the text wraps onto a second line,
// balanced and centred, the button grows in height (the sizes below keep their minimum height) and it is never wider
// than its container.
const baseStyle = defineStyle({
  minW: 0,
  maxW: 'full',
  fontWeight: 'normal',
  borderRadius: 'sm',
  fontSize: 'sm',
  whiteSpace: 'normal',
  textAlign: 'center',
  textWrap: 'balance',
  // `break-word`, not `anywhere`: a word breaks only when it cannot fit, and the button's minimum width stays its
  // longest word (an auto-sized grid column or flex item never squeezes "Back" into "Bac / k").
  overflowWrap: 'break-word',
  lineHeight: '1.3',
  // A disabled button stays readable (label and border at least 3:1) instead of a 50 % ghost of the enabled one, but
  // never looks heavier than an enabled one (R113): no fill, a solid outline, the muted label. A loading button keeps
  // Chakra's look.
  '&:is(:disabled, [data-disabled]):not([data-loading])': {
    opacity: 1,
    bg: 'transparent',
    color: 'fg.muted',
    borderColor: 'border.control',
    boxShadow: 'none',
    _hover: { bg: 'transparent' },
  },
  _currentPage: {
    fontWeight: 'bold',
    backgroundColor: {
      _dark: 'colorPalette.800',
      _light: 'colorPalette.200',
    },
  },
  _selected: {
    fontWeight: 'bold',
  },
})

const listmenu = defineStyle({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 'normal',
  borderRadius: 'sm',
  _active: {
    fontWeight: 'bold',
  },
  _hover: {
    bg: 'bg.muted',
  },
})

const profilemenu = defineStyle({
  w: 'full',
  px: 2,
  py: 1.5,
  borderRadius: 'xs',
  display: 'flex',
  justifyContent: 'start',
  bg: 'transparent',
  _hover: {
    bg: 'bg.muted',
  },
})

const unstyled = defineStyle({
  textAlign: 'left',
})

const link = defineStyle({
  textAlign: 'left',
  w: 'auto',
  h: 'auto',
  p: 0,
  verticalAlign: 'unset',
  _hover: {
    textDecoration: 'underline',
  },
})

const navbar = defineStyle({
  textAlign: 'left',
  fontWeight: 'semibold',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  fontSize: 'md',
  h: 'fit-content',
})

/** `h: auto` with a minimum height: a wrapped label makes the button taller instead of overflowing it. */
const grow = (minH: string, extra: Record<string, unknown> = {}) => ({ h: 'auto', minH, py: 1.5, ...extra })

/**
 * The button hierarchy (R113): one look per rank, so a person can tell an action from a link or from text at a glance.
 * - `solid` (primary): the accent fill (Chakra's default), the main action of a screen; in dark mode with an accent edge.
 * - `outline` (secondary): a neutral content-surface fill with a `border.input` outline (at least 3:1 on every surface)
 *   and a soft shadow, so it reads as a button on the canvas, on a card and on the chrome alike; the accent label.
 * - `ghost` (tertiary): no fill until hover or focus, then the palette's tint; used with an icon (Back, Log out).
 * - `colorPalette='red'`: destructive (solid or outline).
 */
const outline = defineStyle({
  bg: 'bg',
  borderColor: 'border.input',
  boxShadow: 'surface',
  _hover: { bg: 'colorPalette.subtle', borderColor: 'colorPalette.solid' },
  _expanded: { bg: 'colorPalette.subtle' },
})

// In dark mode the deep accent fill is under 3:1 on the lighter content surfaces: its `border` step (3:1 on every
// surface) draws the edge. In light mode the fill itself is the edge.
const solid = defineStyle({
  _dark: { borderColor: 'colorPalette.border' },
})

const ghost = defineStyle({
  _hover: { bg: 'colorPalette.subtle' },
  _focusVisible: { bg: 'colorPalette.subtle' },
})

export const Button = defineRecipe({
  base: baseStyle,
  variants: {
    // Default-size buttons are 48 px touch targets on the member pages (`--ipc-control-h`, spec Goal 7) and
    // vocdoni-app's 40 px in the admin area; `lg` is the 48 px main action of a member screen. `sm` and `xs` are 48 px
    // on the member pages too (`--ipc-control-h-sm`, set outside the admin area only) and keep Chakra's 36 / 32 px in
    // the admin area.
    size: {
      '2xs': grow('6'),
      xs: grow('var(--ipc-control-h-sm, 2rem)', { minW: 'var(--ipc-control-h-sm, 2rem)' }),
      sm: grow('var(--ipc-control-h-sm, 2.25rem)', { minW: 'var(--ipc-control-h-sm, 2.25rem)' }),
      md: grow('var(--ipc-control-h)', { px: 4, fontSize: 'sm' }),
      lg: grow('12', { px: 6, fontSize: 'md', py: 2 }),
      xl: grow('12'),
      '2xl': grow('16'),
    },
    variant: {
      solid,
      outline,
      ghost,
      unstyled,
      navbar,
      link,
      listmenu,
      profilemenu,
    },
  },
})
